import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle, ArrowRight, Phone, MessageCircle, Mail,
  HardHat, Briefcase, Plane, MapPin, Globe2, Sparkles, Flame, Star,
  Landmark, BadgeCheck, Building2, Cpu, RotateCcw, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import { useContactChannel } from '@/hooks/useContactChannel';

/**
 * /saudi-arabia  — Saudi Visa Hub Landing Page (Feb 2026)
 * Replaces the legacy generic SaudiArabiaPage. Acts as the topic-authority
 * hub linking Work / Business / Tourist visa pillar pages with an
 * interactive 5-question Visa Wizard for guided routing.
 */

const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const VISA_CARDS = [
  {
    key: 'work',
    icon: HardHat,
    badgeIcon: Flame,
    badgeText: 'Most Popular',
    badgeTone: 'bg-orange-100 text-orange-800 border-orange-200',
    accent: 'from-amber-500 to-amber-600',
    title: 'Saudi Work Visa from India 2026',
    subtitle: 'Professional · Engineer · Technician · Nurse · Labour',
    features: [
      '98% Success Rate',
      'QVP for Technician Visas',
      'MEA & Qiwa Compliant',
      '35–50 Days Processing',
    ],
    pricing: 'Dynamic Pricing — Get Quote',
    cta: 'Apply for Work Visa',
    link: '/saudi-arabia/work-visa',
  },
  {
    key: 'business',
    icon: Briefcase,
    badgeIcon: Sparkles,
    badgeText: 'New 2026',
    badgeTone: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    accent: 'from-emerald-500 to-emerald-600',
    title: 'Saudi Business Visit Visa 2026',
    subtitle: '1-Year Multiple Entry · 90 Days Per Visit',
    features: [
      '100% Online Process',
      'No Invitation Letter Needed',
      '7–10 Days Processing',
      'Valid for 1 Year',
    ],
    pricing: 'Dynamic Pricing — Get Quote',
    cta: 'Apply for Business Visa',
    link: '/saudi-arabia/business-visa',
  },
  {
    key: 'tourist',
    icon: Plane,
    badgeIcon: Star,
    badgeText: 'Easy Process',
    badgeTone: 'bg-blue-100 text-blue-800 border-blue-200',
    accent: 'from-blue-500 to-blue-600',
    title: 'Saudi Tourist Visa from India 2026',
    subtitle: '30 Days Single Entry · Family Friendly',
    features: [
      '100% Online Application',
      'Family Included',
      '5–7 Days Processing',
      'Valid for 90 Days',
    ],
    pricing: 'Dynamic Pricing — Get Quote',
    cta: 'Apply for Tourist Visa',
    link: '/saudi-arabia/tourist-visa',
  },
];

const WIZARD_QUESTIONS = [
  {
    q: 'What is your primary purpose for visiting Saudi Arabia?',
    options: [
      { text: 'Working / Employment', suggests: 'work' },
      { text: 'Business Meeting / Conference', suggests: 'business' },
      { text: 'Tourism / Family Visit', suggests: 'tourist' },
      { text: 'Education / Study', suggests: 'work' },
    ],
  },
  {
    q: 'How long do you plan to stay in Saudi Arabia?',
    options: [
      { text: '30 Days or Less', suggests: 'tourist' },
      { text: '90 Days', suggests: 'business' },
      { text: '6 Months to 2 Years', suggests: 'work' },
      { text: '1 Year Multiple Entry', suggests: 'business' },
    ],
  },
  {
    q: 'Do you have a Saudi employer or sponsor?',
    options: [
      { text: 'Yes, I have a job offer', suggests: 'work' },
      { text: 'Yes, I have a business invitation', suggests: 'business' },
      { text: 'No, I am visiting independently', suggests: 'tourist' },
      { text: 'No, but I am sponsored by family', suggests: 'tourist' },
    ],
  },
  {
    q: 'What is your profession / qualification?',
    options: [
      { text: 'Engineer (B.Tech / BE)', suggests: 'work' },
      { text: 'Technician (Diploma / ITI)', suggests: 'work' },
      { text: 'Nurse (B.Sc. Nursing)', suggests: 'work' },
      { text: 'Business Owner / Executive', suggests: 'business' },
      { text: 'General Labour / Tradesman', suggests: 'work' },
      { text: 'Student / Tourist', suggests: 'tourist' },
    ],
  },
  {
    q: 'How quickly do you need your visa?',
    options: [
      { text: '5–7 Days (Fastest)', suggests: 'tourist' },
      { text: '7–10 Days', suggests: 'business' },
      { text: '35–50 Days (Standard)', suggests: 'work' },
      { text: '60–90 Days (if PCC required)', suggests: 'work' },
    ],
  },
];

const RESULT_CARDS = {
  work: {
    icon: HardHat,
    title: 'Saudi Work Visa',
    summary: 'Best for employment in Saudi Arabia. Requires Qiwa contract, QVP (for Technician roles), and degree attestation.',
    benefits: ['98% Success Rate', 'MEA & Qiwa Compliant', '35–50 Days Processing'],
    cta: 'Apply for Work Visa',
    link: '/saudi-arabia/work-visa',
    accent: 'from-amber-500 to-amber-600',
  },
  business: {
    icon: Briefcase,
    title: 'Saudi Business Visit Visa',
    summary: 'Best for business meetings, conferences and professional events. 1-Year multiple entry, 90 days per visit.',
    benefits: ['100% Online Process', 'No Invitation Letter Needed', '7–10 Days Processing'],
    cta: 'Apply for Business Visa',
    link: '/saudi-arabia/business-visa',
    accent: 'from-emerald-500 to-emerald-600',
  },
  tourist: {
    icon: Plane,
    title: 'Saudi Tourist Visa',
    summary: 'Best for tourism and family visits. 30 days single entry, family-friendly, 100% online application.',
    benefits: ['100% Online Application', 'Family Included', '5–7 Days Processing'],
    cta: 'Apply for Tourist Visa',
    link: '/saudi-arabia/tourist-visa',
    accent: 'from-blue-500 to-blue-600',
  },
};

const INTERNAL_LINKS = [
  {
    icon: HardHat,
    title: 'Saudi Work Visa from India',
    description: 'Professional, Engineer, Technician, Nurse, Labour visas. QVP mandatory for Technician visas. MEA & Qiwa compliant. 98% success rate.',
    links: [
      { text: 'Saudi Work Visa Requirements', url: '/saudi-arabia/work-visa' },
      { text: 'QVP for Technician Visa', url: '/saudi-arabia/work-visa' },
      { text: 'Processing Time & Fees', url: '/saudi-arabia/work-visa' },
    ],
    cta: 'Learn More about Work Visa',
    link: '/saudi-arabia/work-visa',
  },
  {
    icon: Briefcase,
    title: 'Saudi Business Visit Visa',
    description: '1-year multiple entry, 90 days per visit. 100% online process. No invitation letter required. 7–10 days processing.',
    links: [
      { text: 'Business Visa Requirements', url: '/saudi-arabia/business-visa' },
      { text: 'Online Application Process', url: '/saudi-arabia/business-visa' },
      { text: 'Cost & Processing Time', url: '/saudi-arabia/business-visa' },
    ],
    cta: 'Learn More about Business Visa',
    link: '/saudi-arabia/business-visa',
  },
  {
    icon: Plane,
    title: 'Saudi Tourist Visa',
    description: '30 days single entry, family-friendly. 100% online application. 5–7 days processing. Valid for 90 days.',
    links: [
      { text: 'Tourist Visa Requirements', url: '/saudi-arabia/tourist-visa' },
      { text: 'Family Visit Guide', url: '/saudi-arabia/tourist-visa' },
      { text: 'Step-by-Step Process', url: '/saudi-arabia/tourist-visa' },
    ],
    cta: 'Learn More about Tourist Visa',
    link: '/saudi-arabia/tourist-visa',
  },
];

const AUTHORITY_CARDS = [
  {
    icon: Landmark,
    title: 'Authorized Agency by Royal Embassy',
    description: 'Direct Embassy submission — no third-party delays. 98% success rate across 400,000+ visas processed.',
    stat: '400,000+ Visas Processed',
  },
  {
    icon: BadgeCheck,
    title: 'Government of India MEA-Approved',
    description: `MEA Registration: ${MEA_REG}. Fully compliant with all government regulations.`,
    stat: '15+ Years Experience',
  },
  {
    icon: Building2,
    title: 'Dedicated Centres in 5 Cities',
    description: 'Delhi, Mumbai, Hyderabad, Chennai, Kolkata. Free document pickup. 24/7 local support.',
    stat: '5 Pan-India Centres',
  },
  {
    icon: Cpu,
    title: 'QVP & Qiwa Platform Mastery',
    description: 'Complete QVP verification for Technician visas. Real-time Qiwa contract verification. 100% digital compliance.',
    stat: 'QVP Expertise',
  },
];

const HUB_FAQS = [
  {
    q: 'Which Saudi visa is right for me?',
    a: 'It depends on your purpose, duration of stay, and sponsor type. Use our 5-question Visa Wizard above to get an instant recommendation. As a rule of thumb: Saudi Work Visa for employment (6 months+), Saudi Business Visit Visa for meetings/conferences (1-year multi-entry), and Saudi Tourist Visa for tourism or family visits (30-day single entry).',
  },
  {
    q: 'Can I apply for Saudi Visa from India without an agent?',
    a: 'For Tourist and Business visas you can technically apply yourself via the online portal, but the Royal Embassy of Saudi Arabia does NOT accept direct individual submissions for Work Visa stamping. Work Visa must go through an Authorized Saudi Visa Processing Agency like ZAFCO. Authorized agencies also get priority queue (3–5 days vs 15–20 days for general applicants).',
  },
  {
    q: 'How long does each Saudi visa take to process?',
    a: 'Saudi Tourist Visa: 5–7 days. Saudi Business Visit Visa: 7–10 days. Saudi Work Visa: 35–50 days standard (60–90 days if PCC required, 41–115 days for Technician with QVP). All timelines assume complete documentation.',
  },
  {
    q: 'What documents are common across all 3 Saudi visa types?',
    a: 'Original passport (valid 6+ months), passport-size photographs, completed visa application form, and proof of purpose (job offer for Work, business invitation for Business, hotel/itinerary for Tourist). Work and Business visas additionally require GAMCA/Wafid medical (Work) and Qiwa contract (Work).',
  },
];

/** Visa Wizard — inline component (only used once on this page) */
const VisaWizard = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);

  const handleAnswer = (opt) => {
    const next = [...answers, opt.suggests];
    if (current < WIZARD_QUESTIONS.length - 1) {
      setAnswers(next);
      setCurrent(current + 1);
    } else {
      setAnswers(next);
      setDone(true);
    }
  };

  const reset = () => {
    setAnswers([]);
    setCurrent(0);
    setDone(false);
  };

  const getResult = () => {
    const tally = answers.reduce((acc, v) => ({ ...acc, [v]: (acc[v] || 0) + 1 }), {});
    const winner = Object.keys(tally).reduce((a, b) => (tally[a] >= tally[b] ? a : b));
    return RESULT_CARDS[winner];
  };

  const result = done ? getResult() : null;
  const Q = WIZARD_QUESTIONS[current];
  const progress = ((current + (done ? 1 : 0)) / WIZARD_QUESTIONS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="saudi-hub-wizard-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-zafco-blue text-2xl font-syne">
            {done ? 'Your Recommended Saudi Visa' : 'Saudi Visa Wizard'}
          </DialogTitle>
          <DialogDescription>
            {done ? 'Based on your 5 answers, this is the best match for your needs.' : 'Answer 5 quick questions to find the right Saudi visa for you.'}
          </DialogDescription>
        </DialogHeader>

        {!done && (
          <div className="space-y-5">
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs font-semibold text-amber-600 tracking-wider uppercase">
              Question {current + 1} of {WIZARD_QUESTIONS.length}
            </p>
            <h3 className="text-xl font-bold text-zafco-blue font-syne">{Q.q}</h3>
            <div className="grid gap-2">
              {Q.options.map((opt) => (
                <button
                  key={opt.text}
                  onClick={() => handleAnswer(opt)}
                  className="w-full text-left rounded-xl border border-slate-200 bg-white hover:border-amber-500 hover:bg-amber-50/40 hover:shadow-sm px-4 py-3 text-sm font-medium text-slate-800 transition-all flex items-center justify-between group"
                  data-testid={`saudi-hub-wizard-option-${opt.suggests}`}
                >
                  <span>{opt.text}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
            {current > 0 && (
              <button
                onClick={() => { setCurrent(current - 1); setAnswers(answers.slice(0, -1)); }}
                className="text-xs text-slate-500 hover:text-amber-600"
                data-testid="saudi-hub-wizard-back"
              >
                ← Back to previous question
              </button>
            )}
          </div>
        )}

        {done && result && (
          <div className="space-y-5">
            <div className={`rounded-2xl bg-gradient-to-br ${result.accent} text-white p-6 shadow-lg`}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <result.icon className="w-7 h-7" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-white/80">Best Match</p>
                  <h3 className="text-2xl font-bold font-syne">{result.title}</h3>
                </div>
              </div>
              <p className="text-white/90 leading-relaxed mb-5">{result.summary}</p>
              <ul className="space-y-2 mb-6">
                {result.benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-white/95">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button asChild className="bg-white text-zafco-blue hover:bg-white/90 font-bold" data-testid="saudi-hub-wizard-result-apply">
                  <Link to={result.link}>
                    {result.cta} <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="bg-transparent text-white border-white hover:bg-white/10" data-testid="saudi-hub-wizard-result-contact">
                  <Link to={`/contact?source=saudi-hub-wizard&visa=${result.cta.toLowerCase().includes('work') ? 'work' : result.cta.toLowerCase().includes('business') ? 'business' : 'tourist'}`}>
                    Start Application
                  </Link>
                </Button>
              </div>
            </div>
            <button
              onClick={reset}
              className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 hover:text-amber-600 py-2"
              data-testid="saudi-hub-wizard-reset"
            >
              <RotateCcw className="w-4 h-4" /> Start Again
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SaudiArabiaPage = () => {
  const ch = useContactChannel();
  const [wizardOpen, setWizardOpen] = useState(false);

  usePageMeta(
    'Saudi Arabia Visa from India 2026 | Authorized Embassy Agency ZAFCO',
    'Complete Saudi Arabia Visa guide for Indians 2026 — Work Visa, Business Visit Visa, Tourist Visa. Authorized Embassy Agency ZAFCO. MEA-approved. 98% success. Start the 5-question visa wizard to find the right visa.',
    {
      canonical: '/saudi-arabia',
      image: '/social-media/zafco-facebook-cover.png',
      imageAlt: 'Saudi Arabia Visa Hub — Work, Business and Tourist visas processed by ZAFCO Authorized Embassy Agency.',
    },
  );

  useFAQSchema(HUB_FAQS);

  // Breadcrumb + Service JSON-LD
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Saudi Arabia', item: 'https://zafco.in/saudi-arabia' },
      ],
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Saudi Arabia Visa Processing for Indians',
      description: 'MEA-approved & Royal Saudi Embassy authorised Saudi visa processing — Work Visa (Professional/Engineer/Technician/Nurse/Labour), Business Visit Visa (1-year multi-entry) and Tourist Visa (30 days). Includes Qiwa/Muqeem compliance, GAMCA/Wafid medical, degree attestation and direct Embassy stamping.',
      serviceType: 'Visa Processing Hub',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [{ '@type': 'Country', name: 'India' }, { '@type': 'Country', name: 'Saudi Arabia' }],
      url: 'https://zafco.in/saudi-arabia',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-saudi-hub-page', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const waMessage = 'Hello ZAFCO, I need help choosing the right Saudi Visa. Please guide me through Work / Business / Tourist visa options.';

  return (
    <div data-testid="saudi-arabia-page">
      <PageHeader
        title="Saudi Arabia Visa from India 2026 — Authorized Embassy Agency ZAFCO"
        subtitle="Work Visa · Business Visit Visa · Tourist Visa — Complete 2026 Guide"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Saudi Arabia', path: '/saudi-arabia' },
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved · Royal Saudi Embassy Authorised Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* SECTION 1 — Hero Trust Bar + Primary CTAs */}
      <section className="py-12 bg-white" aria-labelledby="saudi-hub-trust">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
              <BadgeCheck className="w-3.5 h-3.5" /> MEA-Approved
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              <Landmark className="w-3.5 h-3.5" /> Authorised Embassy Agency
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
              <CheckCircle className="w-3.5 h-3.5" /> 98% Success Rate
            </span>
          </div>
          <h2 id="saudi-hub-trust" className="sr-only">Saudi Visa Hub Trust Bar</h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="saudi-hub-cta-quote">
              <Link to="/contact?source=saudi-hub&intent=quote">
                Get Customised Quote <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
            </Button>
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold"
              data-testid="saudi-hub-cta-wizard-hero"
            >
              <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" /> Start Visa Wizard
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="saudi-hub-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" /> WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Executive Overview + Stats */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-hub-overview">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Vision 2030 · Saudi Talent Migration</span>
          <h2 id="saudi-hub-overview" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Why Saudi Arabia? Vision 2030&rsquo;s Unprecedented Opportunity Wave
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Kingdom of Saudi Arabia has emerged as the world&rsquo;s most dynamic destination for Indian professionals, families, and travellers — driven by the <strong>$1.3 trillion Vision 2030</strong> transformation agenda. From mega-projects like NEOM and the Red Sea Project to a booming tourism sector that welcomed 100M+ visitors in 2024, Saudi Arabia is opening its doors wider than ever to Indian nationals.
          </p>
          <p className="text-gray-700 leading-relaxed mb-8">
            As of 2026, over <strong>1.8 million Indian nationals</strong> are legally employed in Saudi Arabia, while millions more visit annually for business, religious pilgrimage, and tourism. The Saudi government has streamlined visa protocols through digital platforms like <strong>Qiwa, Muqeem and the Visit Saudi e-portal</strong>, reducing processing timelines by 60%.
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { num: '2.5M+', label: 'Job Opportunities by 2030', icon: Briefcase },
              { num: '35%', label: 'Indian Share of Expat Workforce', icon: Globe2 },
              { num: '60%', label: 'Faster Processing via Digital Platforms', icon: Cpu },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-zafco-blue/10 flex items-center justify-center mx-auto mb-3">
                  <s.icon className="w-6 h-6 text-zafco-blue" aria-hidden="true" />
                </div>
                <p className="text-3xl font-bold text-zafco-blue font-syne">{s.num}</p>
                <p className="text-sm text-gray-600 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — 3 Visa Types Card Grid (CORE FEATURE) */}
      <section className="py-16 bg-white" aria-labelledby="saudi-hub-visa-cards">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-10">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Visa Categories</span>
            <h2 id="saudi-hub-visa-cards" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-3">
              Choose Your Saudi Visa Type
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Three visa pathways into Saudi Arabia — each with distinct eligibility, processing time and compliance requirements. Tap a card to dive into the full pillar guide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {VISA_CARDS.map((card) => {
              const Icon = card.icon;
              const BadgeIcon = card.badgeIcon;
              return (
                <motion.div
                  key={card.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="relative rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all p-6 flex flex-col"
                  data-testid={`saudi-hub-visa-card-${card.key}`}
                >
                  {/* Badge */}
                  <span className={`absolute -top-3 left-6 inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full border ${card.badgeTone}`}>
                    <BadgeIcon className="w-3.5 h-3.5" /> {card.badgeText}
                  </span>

                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.accent} text-white flex items-center justify-center mb-4 shadow-sm`}>
                    <Icon className="w-7 h-7" aria-hidden="true" />
                  </div>

                  <h3 className="font-bold text-zafco-blue text-lg leading-snug mb-1.5">{card.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{card.subtitle}</p>

                  <ul className="space-y-2 mb-5 flex-1">
                    {card.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <p className="text-xs font-semibold text-amber-700 mb-3">{card.pricing}</p>

                  <Button asChild className="w-full bg-zafco-blue hover:bg-zafco-blue/90" data-testid={`saudi-hub-visa-card-${card.key}-cta`}>
                    <Link to={card.link}>
                      {card.cta} <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                    </Link>
                  </Button>
                </motion.div>
              );
            })}
          </div>

          {/* Wizard nudge */}
          <div className="mt-10 rounded-2xl bg-gradient-to-r from-zafco-blue to-zafco-blue/90 text-white p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-1">Not sure which visa?</p>
              <h3 className="text-2xl md:text-3xl font-bold font-syne">Which Saudi visa is right for you?</h3>
              <p className="text-white/85 text-sm mt-1.5">Answer 5 quick questions — get an instant personalised recommendation.</p>
            </div>
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold whitespace-nowrap"
              data-testid="saudi-hub-cta-wizard-banner"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Start Wizard
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Wizard (modal trigger lives in CTA banners) */}
      <VisaWizard open={wizardOpen} onOpenChange={setWizardOpen} />

      {/* SECTION 5 — Internal Linking Boost */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-hub-deep-links">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-10">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Complete Saudi Visa Guide</span>
            <h2 id="saudi-hub-deep-links" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-3">
              Dive Deeper Into Each Visa Type
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Full breakdowns of eligibility, documents, attestation, medical and processing for each Saudi visa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {INTERNAL_LINKS.map((block) => {
              const Icon = block.icon;
              return (
                <div
                  key={block.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6"
                  data-testid={`saudi-hub-link-block-${block.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-zafco-blue/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-zafco-blue" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-zafco-blue text-lg mb-2">{block.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">{block.description}</p>
                  <ul className="space-y-1.5 mb-5">
                    {block.links.map((l) => (
                      <li key={l.text}>
                        <Link
                          to={l.url}
                          className="text-sm text-zafco-blue hover:text-amber-600 inline-flex items-center gap-1.5 font-medium"
                        >
                          <CheckCircle className="w-3.5 h-3.5 text-green-600" aria-hidden="true" /> {l.text}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={block.link}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 group"
                  >
                    {block.cta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — ZAFCO Authority */}
      <section className="py-16 bg-white" aria-labelledby="saudi-hub-authority">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-10">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Brand Trust</span>
            <h2 id="saudi-hub-authority" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2">
              Why Choose ZAFCO for Saudi Visas?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {AUTHORITY_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zafco-blue text-white flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-zafco-blue mb-1">{c.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-2">{c.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                      <Star className="w-3.5 h-3.5" /> {c.stat}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 7 — Quick Hub FAQs */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-hub-faqs">
        <div className="container mx-auto px-6 max-w-4xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Hub FAQs</span>
          <h2 id="saudi-hub-faqs" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Saudi Visa Hub — Quick Answers
          </h2>
          <div className="space-y-3">
            {HUB_FAQS.map((item, idx) => (
              <details
                key={item.q}
                className="group rounded-xl border border-slate-200 bg-white open:shadow-sm"
                data-testid={`saudi-hub-faq-${idx}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-5 font-semibold text-zafco-blue">
                  <span className="flex-1">Q{idx + 1}: {item.q}</span>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-700 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — Consultation Form */}
      <section className="py-16 bg-white" aria-labelledby="saudi-hub-form">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Apply Now</span>
              <h2 id="saudi-hub-form" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
                Start your Saudi visa application today
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As the only officially Authorized Agency by the Royal Embassy of Saudi Arabia for visa stamping in India, ZAFCO delivers end-to-end Saudi Visa Processing across all 3 visa types — Work, Business Visit and Tourist.
              </p>
              <ul className="space-y-2 text-sm text-gray-700 mb-6">
                {[
                  'Direct Embassy submission — priority queue access',
                  'Free document pickup across 5 Pan-India centres',
                  'Real-time Qiwa & Muqeem verification (Work Visa)',
                  '5–7 day Tourist Visa processing',
                  'Customised pricing — no fixed online quotes',
                  '24/7 expert support in Hindi & English',
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> {p}</li>
                ))}
              </ul>
              <Button
                onClick={() => setWizardOpen(true)}
                variant="outline"
                className="border-amber-600 text-amber-700 hover:bg-amber-50"
                data-testid="saudi-hub-cta-wizard-form"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Not sure? Run the Visa Wizard
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-theme="light">
              <ConsultationForm source="saudi-arabia-hub" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — Final CTA */}
      <section className="py-16 bg-zafco-blue text-white" aria-labelledby="saudi-hub-final-cta">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 id="saudi-hub-final-cta" className="text-3xl md:text-4xl font-bold font-syne mb-4">
            Ready to Apply for Your Saudi Visa?
          </h2>
          <p className="text-white/90 leading-relaxed mb-6 max-w-2xl mx-auto">
            Get real-time visa fees, complete cost breakdown, and a personalised processing timeline from ZAFCO&rsquo;s Saudi Visa Consultants.
          </p>
          <div className="grid md:grid-cols-3 gap-3 text-sm text-white/90 mb-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> <a href={ch.phoneTelUrl} className="hover:underline">{ch.phoneDisplay}</a></div>
            <div className="flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> <a href={`mailto:${ch.email}`} className="hover:underline">{ch.email}</a></div>
            <div className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4" /> New Friends Colony, New Delhi 110025</div>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-hub-final-cta-quote">
              <Link to="/contact?source=saudi-hub&intent=apply">Get Customised Quote <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button
              onClick={() => setWizardOpen(true)}
              variant="outline"
              className="bg-white text-zafco-blue border-white hover:bg-white/90"
              data-testid="saudi-hub-final-cta-wizard"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Start Visa Wizard
            </Button>
            <Button asChild variant="outline" className="bg-transparent text-white border-white hover:bg-white/10" data-testid="saudi-hub-final-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp {ch.whatsappDisplay}</a>
            </Button>
          </div>

          {/* Quick visa links */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-5 max-w-3xl mx-auto">
            <p className="text-xs font-bold text-amber-300 tracking-widest uppercase mb-3">Quick Visa Links</p>
            <div className="grid sm:grid-cols-3 gap-2 text-sm">
              <Link to="/saudi-arabia/work-visa" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2.5 transition-colors" data-testid="saudi-hub-quicklink-work">
                <HardHat className="w-4 h-4" /> Work Visa
              </Link>
              <Link to="/saudi-arabia/business-visa" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2.5 transition-colors" data-testid="saudi-hub-quicklink-business">
                <Briefcase className="w-4 h-4" /> Business Visa
              </Link>
              <Link to="/saudi-arabia/tourist-visa" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2.5 transition-colors" data-testid="saudi-hub-quicklink-tourist">
                <Plane className="w-4 h-4" /> Tourist Visa
              </Link>
            </div>
          </div>

          <p className="text-xs text-white/60 mt-8">
            ZAFCO Human Resource Management · MEA Registration: {MEA_REG} · Authorised Agency by the Royal Embassy of Saudi Arabia · Government of India Approved · 15+ Years Experience · 98% Success Rate · 400,000+ Visas Processed
          </p>
        </div>
      </section>
    </div>
  );
};

export default SaudiArabiaPage;
